import { Checkbox, Dropdown, message, Modal, Table, Tooltip } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import previewIcon from "../../../../../Assets/svg/table-action-preview.svg";
import editIcon from "../../../../../Assets/svg/table-action-edit.svg";
import Filters from "./Filters";
import Preview from "../../../planning/VisitBoard/TableView/modals/Preview";
import EditSiteVisitCardModal from "../../../planning/VisitBoard/Modals/EditSiteVisitCardModal";
import EditDataChecksModal from "../../../reporting/DataChecks/Edit";
import AddTrackingCardModal from "../../../planning/VisitBoard/Modals/AddTrackingCardModal";
import EditReportingModal from "../../../reporting/ReportingList/modals/Edit";
import RejectModal from "../../../reporting/approveSiteVisits/RejectModal";
import { httpService } from "../../../../../services/httpService.service";
import {
  getSiteVisitStatus,
  siteVisitStatusEnum,
} from "../../../../../services/Enums/siteVisitStatusEnum";
import {
  fieldTeamReportingStatusEnum,
  getFieldTeamReportingStatus,
} from "../../../../../services/Enums/fieldTeamReportingStatusEnum";
import { months, years } from "../../../../../services/Enums/monthsAndYears";
import FacilityTag from "../../../../components/FacilityTag/FacilityTag";
import {
  InlineDate,
  InlineNumber,
  InlineSelect,
  InlineText,
} from "../../../../components/InlineEdit/InlineEdit";
import "../../../../components/InlineEdit/inlineEdit.scss";
import TableScrollSync from "../../../../components/TableScrollSync/TableScrollSync";
import {
  inlineUpdateErrorText,
  saveFieldTeamReportingStatus,
  saveSiteVisitActivationDate,
  saveSiteVisitBeneficiariesTarget,
  saveSiteVisitClientInstructions,
  saveSiteVisitCluster,
  saveSiteVisitCoordinationType,
  saveSiteVisitCoordinator,
  saveSiteVisitDataCollectionLink,
  saveSiteVisitFacility,
  saveSiteVisitFulcrumAccount,
  saveSiteVisitKiiTarget,
  saveSiteVisitMediaLimit,
  saveSiteVisitName,
  saveSiteVisitNotes,
  saveSiteVisitObservationTarget,
  saveSiteVisitOtherInstructions,
  saveSiteVisitPhotos,
  saveSiteVisitPlanApproval,
  saveSiteVisitPlannedVisit,
  saveSiteVisitPostponed,
  saveSiteVisitPostponedDetails,
  saveSiteVisitPostponedReason,
  saveSiteVisitSamplingInstructions,
  saveSiteVisitSpecifics,
  saveSiteVisitStatus,
  saveSiteVisitTeam,
  saveSiteVisitTeamLocal,
  saveSiteVisitVideos,
  saveSiteVisitVirtual,
  saveTrackingActualDateOfVisit,
  saveTrackingDateOfDebrief,
  saveTrackingDateOfReport,
  saveTrackingPercentageCompleted,
  saveTrackingReportingMonth,
  saveTrackingReportingYear,
  saveTrackingTrainingCompleted,
  saveTrackingTrainingDate,
  saveTrackingTrainingScoreInfo,
} from "../../../../../services/siteVisitInlineUpdate.service";
import { RolesEnum } from "../../../../../services/rolesEnum";
import { isAuthorized } from "../../../../../services/authService";
import { orderSiteVisitColumns } from "../../../../components/SiteVisitColumns/siteVisitColumnOrder";
import { useColumnSizing } from "../../../../components/SiteVisitColumns/useColumnSizing";
import {
  APPROVAL_STATUS,
  approveSiteVisit,
  markSiteVisitCompleted,
  nextSiteVisitStep,
  submitSiteVisitForApproval,
} from "../../../../../services/siteVisitWorkflow.service";
import {
  FiCheck,
  FiCheckSquare,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import dayjs from "dayjs";

/**
 * Site Visit Summary — the whole site-visit process, edited in place.
 *
 * The field set is the union of everything the three phase screens let you edit
 * on a site visit, grouped under the phase that owns it:
 *
 *   Planning        planning/VisitBoard/Modals/EditSiteVisitCardModal.jsx
 *                   (identity, facility/cluster, team + coordination, interview
 *                   targets, instructions)
 *   Implementation  implementation/VisitBoard/TableView/modals/Edit.jsx
 *                   (reporting period, training, actual visit, % collected,
 *                   postponement)
 *   Reporting       reporting/ReportingList/modals/Edit.jsx
 *                   (field report status, report/debrief dates, dataset link,
 *                   required photos and videos)
 *
 * Deliberately left to the full card modal, because a cell cannot edit them
 * safely rather than because they were missed:
 *   - IP plan, sector and sub-sector cascade into each other
 *   - the geocoded location block is validated as a whole against the address
 *     lookup, so a single field cannot be saved on its own
 *   - contacts, enumerators, required reports and file uploads are lists
 *   - data-check *actuals* (conducted counts) are one payload per visit, so
 *     they open the reporting Data Checks modal from the row actions instead
 *   - approval status is a gated transition (rejection needs a reason), so it
 *     is shown read-only and moved from the approval screens
 */

// Roles the Visits Board grants the edit action to; inline editing exposes the
// same surface, no more.
const EDIT_ROLES = [
  RolesEnum.ProjectManager,
  RolesEnum.SystemAdmin,
  RolesEnum.FieldCoordinator,
  RolesEnum.FieldTeamLeader,
  RolesEnum.FieldLogistics,
  RolesEnum.DataAnalyst,
];

// Approving, rejecting and closing a visit out are gated to the same two roles
// the Approve Site Visits screen is gated to — the summary is a shortcut to that
// workflow, not a way around its permissions.
const APPROVE_ROLES = [RolesEnum.ProjectManager, RolesEnum.SystemAdmin];

const DATE_FORMAT = "DD MMM YYYY";

// Long free text is the only thing that gets capped — everything else sizes to
// its content so nothing is cut off.
const TEXT_COL_MAX = 240;

/**
 * Column widths, sized to the widest value each column can actually hold rather
 * than to a uniform number.
 *
 * The table runs at `table-layout: auto`, so these act as a baseline the browser
 * may grow but never shrink — the status columns are wide enough for their
 * longest labels ("Post data collection/Data check", "Field report submitted to
 * client") so a pill is never cut in half.
 */
const COL_W = {
  identifier: 175,
  name: 265,
  siteVisitStatus: 265,
  // Planning
  plannedVisit: 175,
  facility: 165,
  cluster: 165,
  tpmCoordinator: 185,
  team: 165,
  isTeamLocal: 120,
  coordinationType: 175,
  isVirtual: 105,
  activationDate: 165,
  visitPlanApproval: 200,
  beneficiariesTarget: 185,
  kiiTarget: 150,
  observationTarget: 200,
  mediaLimit: 135,
  samplingInstructions: 265,
  clientInstructions: 265,
  otherInstructions: 265,
  specifics: 265,
  fulcrumAccount: 265,
  // Implementation
  reportingMonth: 160,
  reportingYear: 140,
  trainingIsCompleted: 170,
  trainingDate: 165,
  trainingScoreInfo: 200,
  actualDateOfVisit: 190,
  percentageCompleted: 200,
  isPostponed: 125,
  postponedReason: 265,
  postponedDetails: 265,
  notes: 265,
  // Reporting
  fieldTeamReportingStatus: 275,
  approvalStatus: 155,
  dateOfReport: 165,
  dateOfDebrief: 165,
  dataCollectionLink: 265,
  photosRequired: 150,
  photoCount: 115,
  videosRequired: 150,
  videoCount: 115,
  // Wide enough for the longest step ("Re-submit for approval") plus its caret:
  // the cell clips with an ellipsis, so a button that overruns it shows stray dots.
  nextStep: 215,
  // Collapsed, the workflow column is just its icon button.
  nextStepCollapsed: 62,
  actions: 120,
};

/**
 * The identity block is pinned to the left, so a row never loses the site visit
 * it belongs to however far the ~40 process columns have been scrolled.
 *
 * antd needs the left-pinned columns to be contiguous from the very first one, so
 * the selection checkbox and the identifier travel with the name rather than the
 * name being pinned on its own — pinning only the name would leave the two
 * columns before it scrolling underneath it.
 *
 * The width is what the pinned block occupies, used to park the phase band labels
 * just clear of it (the checkbox column is generously estimated: overshooting only
 * shifts a label a few pixels right, undershooting would hide it behind the block).
 */
const SELECTION_COL_W = 56;
const FROZEN_LEFT_W = SELECTION_COL_W + COL_W.identifier + COL_W.name;

/**
 * Which phase blocks are on screen. Everything is on by default — the point of
 * the page is that the whole process is in one table — and the choice is
 * remembered so someone who works in one phase isn't re-hiding the others every
 * visit.
 */
const PHASES = [
  { key: "planning", label: "Planning" },
  { key: "implementation", label: "Implementation" },
  { key: "reporting", label: "Reporting" },
];

const PHASE_PREF_KEY = "siteVisitSummary.visiblePhases";

// Whether the pinned workflow column is collapsed to its icon. Pinned columns are
// the ones that cost the scrollable area its width, so this is the one column
// worth being able to fold away — and like the phase choice it is remembered.
const NEXT_STEP_PREF_KEY = "siteVisitSummary.nextStepCollapsed";

const readNextStepPref = () => {
  try {
    return localStorage.getItem(NEXT_STEP_PREF_KEY) === "true";
  } catch {
    return false;
  }
};

const readPhasePref = () => {
  const allOn = { planning: true, implementation: true, reporting: true };
  try {
    const stored = JSON.parse(localStorage.getItem(PHASE_PREF_KEY));
    if (!stored || typeof stored !== "object") return allOn;
    return PHASES.reduce(
      (acc, phase) => ({
        ...acc,
        [phase.key]: stored[phase.key] !== false,
      }),
      {}
    );
  } catch {
    return allOn;
  }
};

const Pill = ({ status }) => (
  <div className={`${status.className} m-0`}>
    <svg xmlns="http://www.w3.org/2000/svg" width="8px" height="8px" viewBox="0 0 9 9">
      <circle cx="4.5" cy="4.27344" r="4" fill={status.fill} />
    </svg>
    <span>{status.text}</span>
  </div>
);

const pillOptions = (enumMap) =>
  Object.entries(enumMap)
    .filter(([key]) => key !== "default")
    .map(([key, value]) => ({
      value: Number(key),
      label: value.text,
      display: <Pill status={value} />,
    }));

const SITE_VISIT_STATUS_OPTIONS = pillOptions(siteVisitStatusEnum);
const FIELD_REPORT_STATUS_OPTIONS = pillOptions(fieldTeamReportingStatusEnum);

const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const COORDINATION_TYPE_OPTIONS = [
  { value: 1, label: "Remote" },
  { value: 2, label: "In-person" },
];

const MONTH_OPTIONS = Object.entries(months).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const YEAR_OPTIONS = years.map((year) => ({ value: year, label: String(year) }));

// How each approval state reads in its column. The transitions themselves live
// in the Next Step control, which uses the numeric APPROVAL_STATUS enum.
const APPROVAL_PILL = {
  1: { text: "Pending", fill: "#FC9403" },
  2: { text: "Approved", fill: "#0E9349" },
  3: { text: "Rejected", fill: "#D4380D" },
};

const coordinationTypeLabel = (value) =>
  COORDINATION_TYPE_OPTIONS.find((option) => option.value === value)?.label;

const personName = (person) => {
  if (!person) return "";
  const { firstName, lastName, email, userName } = person;
  if (firstName || lastName) return `${firstName || ""} ${lastName || ""}`.trim();
  return email || userName || "";
};

const text = (value, fallback = "—") => (
  <span className="styledDataIndex3">{value || fallback}</span>
);

const formatDate = (value) => {
  const date = value ? dayjs(value) : null;
  return date?.isValid() ? date.format(DATE_FORMAT) : null;
};

// Sorters
const byText = (get) => (a, b) => (get(a) || "").localeCompare(get(b) || "");
const byNumber = (get) => (a, b) => (get(a) || 0) - (get(b) || 0);
const byBool = (get) => (a, b) => Number(!!get(a)) - Number(!!get(b));

function TableView({
  data,
  setPagination,
  loading,
  filters,
  setFilters,
  onPageChange,
  pagination,
  onRowSelectionChange,
  onRefresh,
}) {
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [dataChecksTarget, setDataChecksTarget] = useState(null);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [visiblePhases, setVisiblePhases] = useState(readPhasePref);
  const [nextStepCollapsed, setNextStepCollapsed] = useState(readNextStepPref);

  // Workflow modals, each keyed by the site visit they were opened for.
  const [trackingTarget, setTrackingTarget] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  // Local copy of the page so an inline edit shows immediately; it re-syncs
  // whenever the parent refetches, letting server truth win.
  const [rows, setRows] = useState(data || []);

  const [facilities, setFacilities] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const [teams, setTeams] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const canEdit = isAuthorized(EDIT_ROLES);

  useEffect(() => {
    setRows(data || []);
  }, [data]);

  useEffect(() => {
    try {
      localStorage.setItem(PHASE_PREF_KEY, JSON.stringify(visiblePhases));
    } catch {
      // A blocked localStorage only costs the remembered choice.
    }
  }, [visiblePhases]);

  useEffect(() => {
    try {
      localStorage.setItem(NEXT_STEP_PREF_KEY, String(nextStepCollapsed));
    } catch {
      // As above — the column still collapses, it just isn't remembered.
    }
  }, [nextStepCollapsed]);

  // Dropdown sources, fetched once and shared by every row.
  useEffect(() => {
    if (!canEdit) return;
    const contractId = localStorage.getItem("currentContractId");
    setOptionsLoading(true);

    let pending = 4;
    const settle = () => {
      pending -= 1;
      if (pending === 0) setOptionsLoading(false);
    };

    httpService.get(
      "/api/Facility",
      (res) => {
        setFacilities(res?.data || []);
        settle();
      },
      settle
    );
    httpService.get(
      "/api/Site/getAllClusters",
      (res) => {
        setClusters(res?.data || []);
        settle();
      },
      settle
    );
    httpService.get(
      `/api/User/getAllusers?RoleIds=${RolesEnum.FieldCoordinator}&IgnorePagination=true`,
      (res) => {
        setCoordinators(res?.data || []);
        settle();
      },
      settle
    );
    httpService.get(
      `/api/User/getAllTeams?contractId=${contractId}`,
      (res) => {
        setTeams((res?.data || []).filter((team) => team?.isActive));
        settle();
      },
      settle
    );
  }, [canEdit]);

  const facilityOptions = useMemo(
    () =>
      facilities.map((facility) => ({
        value: facility.id,
        label: facility.name,
        display: <FacilityTag facility={facility} />,
      })),
    [facilities]
  );

  const clusterOptions = useMemo(
    () => clusters.map((cluster) => ({ value: cluster.id, label: cluster.name })),
    [clusters]
  );

  const coordinatorOptions = useMemo(
    () =>
      coordinators.map((user) => ({
        value: user.id,
        label: personName(user.userDetails) || user.userName || "Unnamed",
      })),
    [coordinators]
  );

  const teamOptions = useMemo(
    () => teams.map((team) => ({ value: team.id, label: team.name })),
    [teams]
  );

  const handleTogglePreviewModal = (siteVisitCardId) => {
    setSelectedSiteVisit(siteVisitCardId);
    setShowPreviewModal(!showPreviewModal);
  };

  const handleOpenEditModal = (siteVisitCardId) => {
    setSelectedSiteVisit(siteVisitCardId);
    setShowEditModal(true);
  };

  // The edit modals report through the platform's MessagePopUp shape; inline
  // editing already uses antd toasts here, so their messages are forwarded to
  // the same place instead of adding a second notification style to the page.
  const setModalMessage = useCallback((next) => {
    if (!next?.trigger) return;
    const body = next.text || next.title;
    if (next.type === "danger") message.error(body);
    else message.success(body);
  }, []);

  /**
   * Apply `patch` to the row straight away, then persist. If the request fails
   * the original row is restored and the error surfaced, so the table never
   * keeps a value the backend rejected.
   *
   * `refresh` is for the few fields the API stores as a list (training dates,
   * actual visit dates): the cell edits the primary entry, so the row is
   * refetched afterwards rather than guessing what the list now holds.
   */
  const commit = useCallback(
    (record, patch, save, successText, { refresh = false } = {}) => {
      const snapshot = record;
      setRows((prev) =>
        prev.map((row) => (row.id === record.id ? { ...row, ...patch } : row))
      );

      return save()
        .then(() => {
          message.success(successText);
          if (refresh) onRefresh?.();
        })
        .catch((error) => {
          setRows((prev) =>
            prev.map((row) => (row.id === record.id ? snapshot : row))
          );
          message.error(
            inlineUpdateErrorText(error, "Could not save your change. Please try again.")
          );
          throw error;
        });
    },
    [onRefresh]
  );

  // Most editable fields sit under a nested object, so patching one means
  // merging into that object rather than replacing it.
  const commitNested = useCallback(
    (record, key, patch, save, successText, options) =>
      commit(
        record,
        { [key]: { ...(record[key] || {}), ...patch } },
        save,
        successText,
        options
      ),
    [commit]
  );

  const commitLogistic = useCallback(
    (record, patch, save, successText) =>
      commitNested(record, "logistic", patch, save, successText),
    [commitNested]
  );

  const commitTracking = useCallback(
    (record, patch, save, successText, options) =>
      commitNested(record, "tracking", patch, save, successText, options),
    [commitNested]
  );

  const commitVisit = useCallback(
    (record, patch, save, successText) =>
      commitNested(record, "visit", patch, save, successText),
    [commitNested]
  );

  // Interview targets live two levels down, under visit.interviewDetails.
  const commitInterview = useCallback(
    (record, patch, save, successText) =>
      commit(
        record,
        {
          visit: {
            ...(record.visit || {}),
            interviewDetails: {
              ...(record.visit?.interviewDetails || {}),
              ...patch,
            },
          },
        },
        save,
        successText
      ),
    [commit]
  );

  const canApprove = isAuthorized(APPROVE_ROLES);

  /**
   * Run a lifecycle transition. These change where a visit sits in the process
   * rather than one of its fields, so unlike the inline cells they confirm first
   * and then refetch — the backend may move the site visit status, the field
   * report status and the approval status together.
   */
  const runTransition = useCallback(
    ({ title, content, okText, action, successText, danger = false }) => {
      Modal.confirm({
        title,
        content,
        okText,
        cancelText: "Cancel",
        okButtonProps: { danger },
        onOk: () =>
          action()
            .then(() => {
              message.success(successText);
              onRefresh?.();
            })
            .catch((error) => {
              message.error(
                inlineUpdateErrorText(
                  error,
                  "Could not complete that step. Please try again."
                )
              );
            }),
      });
    },
    [onRefresh]
  );

  /**
   * Every step available on a row, in lifecycle order. The first entry is what
   * the row's button does; the rest sit behind its caret, so someone can still
   * reject a report or re-open the tracking card out of sequence.
   */
  const workflowActions = (record) => {
    const approval = record?.approvalStatus;
    const hasTracking = !!record?.tracking?.id;
    const completed = record?.siteVisitStatus === 6;
    const name = record?.identifier || record?.name || "this site visit";

    const actions = [];

    if (canEdit) {
      actions.push({
        key: hasTracking ? "editTracking" : "addTracking",
        label: hasTracking ? "Edit tracking" : "Add tracking",
        run: () => setTrackingTarget({ id: record.id, tracked: hasTracking }),
      });
      actions.push({
        key: "report",
        label: "Add / edit report",
        run: () => setReportTarget(record.id),
      });
      if (record?.visit?.id) {
        actions.push({
          key: "dataChecks",
          label: "Edit data checks",
          run: () =>
            setDataChecksTarget({
              siteVisitId: record.id,
              visitId: record.visit.id,
            }),
        });
      }
    }

    if (canEdit && !completed && approval !== APPROVAL_STATUS.approved) {
      const resubmit = approval === APPROVAL_STATUS.rejected;
      actions.push({
        key: "submit",
        label: resubmit ? "Re-submit for approval" : "Submit for approval",
        run: () =>
          runTransition({
            title: resubmit
              ? `Re-submit ${name} for approval?`
              : `Submit ${name} for approval?`,
            content:
              "The field report is handed to the approvers and the visit shows as pending.",
            okText: resubmit ? "Re-submit" : "Submit",
            action: () => submitSiteVisitForApproval(record.id),
            successText: "Submitted for approval.",
          }),
      });
    }

    if (canApprove && approval === APPROVAL_STATUS.pending) {
      actions.push({
        key: "approve",
        label: "Approve field report",
        run: () =>
          runTransition({
            title: `Approve the field report for ${name}?`,
            content: "The visit can then be marked completed.",
            okText: "Approve",
            action: () => approveSiteVisit(record.id),
            successText: "Field report approved.",
          }),
      });
      actions.push({
        key: "reject",
        label: "Reject field report",
        danger: true,
        run: () => setRejectTarget(record.id),
      });
    }

    if (canApprove && approval === APPROVAL_STATUS.approved && !completed) {
      actions.push({
        key: "complete",
        label: "Mark visit completed",
        run: () =>
          runTransition({
            title: `Mark ${name} as completed?`,
            content:
              "This closes the site visit out — its status becomes Visit completed.",
            okText: "Mark completed",
            action: () => markSiteVisitCompleted(record.id),
            successText: "Site visit completed.",
          }),
      });
    }

    return actions;
  };

  /**
   * Tracking-owned cells. Reporting period, training, actual visit and report
   * dates all belong to the tracking record — until tracking exists there is
   * nothing to edit, the same guard the Field Report Status column has always
   * used.
   */
  const trackingCell = (record, render) =>
    record?.tracking?.id ? (
      render()
    ) : (
      <Tooltip title="Add tracking to this site visit before editing its tracking fields.">
        {text(null, "No tracking")}
      </Tooltip>
    );

  /* ---------------------------------------------------------------- *
   * Identification — always on screen
   * ---------------------------------------------------------------- */
  const identityColumns = [
    // The only read-only column here: the identifier is server-generated.
    {
      title: <span className="styledTitle">Site Visit Identifier</span>,
      dataIndex: "identifier",
      key: "identifier",
      width: COL_W.identifier,
      fixed: "left",
      sorter: byText((row) => row.identifier),
      render: (identifier) => text(identifier),
    },
    {
      title: <span className="styledTitle">Site Visit Name</span>,
      dataIndex: "name",
      key: "name",
      width: COL_W.name,
      fixed: "left",
      sorter: byText((row) => row.name),
      render: (name, record) => (
        <InlineText
          value={name}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          onSave={(next) =>
            commit(
              record,
              { name: next },
              () => saveSiteVisitName(record.id, next),
              "Site visit name updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Site Visit Status</span>,
      dataIndex: "siteVisitStatus",
      key: "siteVisitStatus",
      width: COL_W.siteVisitStatus,
      sorter: byNumber((row) => row.siteVisitStatus),
      render: (_, record) => (
        <InlineSelect
          value={record?.siteVisitStatus}
          options={SITE_VISIT_STATUS_OPTIONS}
          disabled={!canEdit}
          display={<Pill status={getSiteVisitStatus(record?.siteVisitStatus)} />}
          dropdownWidth={260}
          minWidth={230}
          onSave={(next) =>
            commit(
              record,
              { siteVisitStatus: next },
              () => saveSiteVisitStatus(record.id, next),
              "Site visit status updated."
            )
          }
        />
      ),
    },
  ];

  /* ---------------------------------------------------------------- *
   * Planning
   * ---------------------------------------------------------------- */
  const planningColumns = [
    {
      title: <span className="styledTitle">Date of Target Visit</span>,
      key: "plannedVisit",
      width: COL_W.plannedVisit,
      sorter: byText((row) => row.visit?.plannedVisit),
      render: (_, record) => (
        <InlineDate
          value={record?.visit?.plannedVisit}
          disabled={!canEdit}
          format={DATE_FORMAT}
          onSave={(next) =>
            commitVisit(
              record,
              { plannedVisit: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
              () => saveSiteVisitPlannedVisit(record.id, next),
              "Date of target visit updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Facility</span>,
      dataIndex: "facility",
      key: "facility",
      width: COL_W.facility,
      sorter: byText((row) => row.facility?.name),
      render: (facility, record) => (
        <InlineSelect
          value={facility?.id}
          options={facilityOptions}
          loading={optionsLoading}
          disabled={!canEdit}
          display={facility?.name ? <FacilityTag facility={facility} /> : text(null)}
          dropdownWidth={240}
          minWidth={190}
          onSave={(next) =>
            commit(
              record,
              { facility: facilities.find((item) => item.id === next) || null },
              () => saveSiteVisitFacility(record.id, next),
              "Facility updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Cluster</span>,
      key: "cluster",
      width: COL_W.cluster,
      sorter: byText((row) => row.cluster?.name),
      render: (_, record) => (
        <InlineSelect
          value={record?.cluster?.id}
          options={clusterOptions}
          loading={optionsLoading}
          disabled={!canEdit}
          display={text(record?.cluster?.name)}
          dropdownWidth={240}
          minWidth={180}
          onSave={(next) =>
            commit(
              record,
              { cluster: clusters.find((item) => item.id === next) || null },
              () => saveSiteVisitCluster(record.id, next),
              "Cluster updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">TPM Coordinator</span>,
      key: "tpmCoordinator",
      width: COL_W.tpmCoordinator,
      sorter: byText((row) => personName(row?.logistic?.tpmCoordinator)),
      render: (_, record) => (
        <InlineSelect
          value={record?.logistic?.tpmCoordinator?.id}
          options={coordinatorOptions}
          loading={optionsLoading}
          disabled={!canEdit}
          display={text(personName(record?.logistic?.tpmCoordinator))}
          dropdownWidth={260}
          minWidth={200}
          onSave={(next) => {
            const picked = coordinators.find((user) => user.id === next);
            return commitLogistic(
              record,
              {
                tpmCoordinator: picked
                  ? { id: picked.id, ...(picked.userDetails || {}) }
                  : null,
              },
              () => saveSiteVisitCoordinator(record.id, next),
              "TPM coordinator updated."
            );
          }}
        />
      ),
    },
    {
      title: <span className="styledTitle">Team</span>,
      key: "team",
      width: COL_W.team,
      sorter: byText((row) => row?.logistic?.team?.name),
      render: (_, record) => (
        <InlineSelect
          value={record?.logistic?.team?.id}
          options={teamOptions}
          loading={optionsLoading}
          disabled={!canEdit}
          display={text(record?.logistic?.team?.name)}
          dropdownWidth={240}
          minWidth={180}
          onSave={(next) =>
            commitLogistic(
              record,
              { team: teams.find((team) => team.id === next) || null },
              () => saveSiteVisitTeam(record.id, next),
              "Team updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Local Team</span>,
      key: "isTeamLocal",
      width: COL_W.isTeamLocal,
      sorter: byBool((row) => row?.logistic?.isTeamLocal),
      render: (_, record) => (
        <InlineSelect
          value={!!record?.logistic?.isTeamLocal}
          options={YES_NO_OPTIONS}
          disabled={!canEdit}
          display={text(record?.logistic?.isTeamLocal ? "Yes" : "No")}
          dropdownWidth={120}
          minWidth={100}
          onSave={(next) =>
            commitLogistic(
              record,
              { isTeamLocal: next },
              () => saveSiteVisitTeamLocal(record.id, next),
              "Local team updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Coordination Type</span>,
      key: "coordinationType",
      width: COL_W.coordinationType,
      sorter: byNumber((row) => row?.logistic?.coordinationType),
      render: (_, record) => (
        <InlineSelect
          value={record?.logistic?.coordinationType}
          options={COORDINATION_TYPE_OPTIONS}
          disabled={!canEdit}
          display={text(coordinationTypeLabel(record?.logistic?.coordinationType))}
          dropdownWidth={160}
          minWidth={140}
          onSave={(next) =>
            commitLogistic(
              record,
              { coordinationType: next },
              () => saveSiteVisitCoordinationType(record.id, next),
              "Coordination type updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Virtual</span>,
      dataIndex: "isVirtual",
      key: "isVirtual",
      width: COL_W.isVirtual,
      sorter: byBool((row) => row.isVirtual),
      render: (isVirtual, record) => (
        <InlineSelect
          value={!!isVirtual}
          options={YES_NO_OPTIONS}
          disabled={!canEdit}
          display={text(isVirtual ? "Yes" : "No")}
          dropdownWidth={120}
          minWidth={100}
          onSave={(next) =>
            commit(
              record,
              { isVirtual: next },
              () => saveSiteVisitVirtual(record.id, next),
              "Virtual updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Activation Date</span>,
      key: "activationDate",
      width: COL_W.activationDate,
      sorter: byText((row) => row?.logistic?.activationDate),
      render: (_, record) => (
        <InlineDate
          value={record?.logistic?.activationDate}
          disabled={!canEdit}
          format={DATE_FORMAT}
          onSave={(next) =>
            commitLogistic(
              record,
              { activationDate: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
              () => saveSiteVisitActivationDate(record.id, next),
              "Activation date updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Visit Plan Approval</span>,
      key: "visitPlanApproval",
      width: COL_W.visitPlanApproval,
      render: (_, record) => (
        <InlineText
          value={record?.visit?.visitPlanApproval}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          allowEmpty
          onSave={(next) =>
            commitVisit(
              record,
              { visitPlanApproval: next },
              () => saveSiteVisitPlanApproval(record.id, next),
              "Visit plan approval updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Beneficiary Surveys</span>,
      key: "beneficiariesTarget",
      width: COL_W.beneficiariesTarget,
      sorter: byNumber((row) => row.visit?.interviewDetails?.beneficiariesCount),
      render: (_, record) => (
        <InlineNumber
          value={record?.visit?.interviewDetails?.beneficiariesCount}
          disabled={!canEdit}
          onSave={(next) =>
            commitInterview(
              record,
              { beneficiariesCount: next },
              () => saveSiteVisitBeneficiariesTarget(record.id, next),
              "Beneficiary survey target updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">KII Contacts</span>,
      key: "kiiTarget",
      width: COL_W.kiiTarget,
      sorter: byNumber((row) => row.visit?.interviewDetails?.clientsCount),
      render: (_, record) => (
        <InlineNumber
          value={record?.visit?.interviewDetails?.clientsCount}
          disabled={!canEdit}
          onSave={(next) =>
            commitInterview(
              record,
              { clientsCount: next },
              () => saveSiteVisitKiiTarget(record.id, next),
              "KII contact target updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Observation Checklists</span>,
      key: "observationTarget",
      width: COL_W.observationTarget,
      sorter: byNumber((row) => row.visit?.interviewDetails?.observationChecklist),
      render: (_, record) => (
        <InlineNumber
          value={record?.visit?.interviewDetails?.observationChecklist}
          disabled={!canEdit}
          onSave={(next) =>
            commitInterview(
              record,
              { observationChecklist: next },
              () => saveSiteVisitObservationTarget(record.id, next),
              "Observation checklist target updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Media Limit</span>,
      key: "mediaLimit",
      width: COL_W.mediaLimit,
      sorter: byNumber((row) => row.visit?.interviewDetails?.mediaLimit),
      render: (_, record) => (
        <InlineNumber
          value={record?.visit?.interviewDetails?.mediaLimit}
          disabled={!canEdit}
          onSave={(next) =>
            commitInterview(
              record,
              { mediaLimit: next },
              () => saveSiteVisitMediaLimit(record.id, next),
              "Media limit updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Sampling Instructions</span>,
      key: "samplingInstructions",
      width: COL_W.samplingInstructions,
      render: (_, record) => (
        <InlineText
          value={record?.visit?.interviewDetails?.samplingInstructions}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commitInterview(
              record,
              { samplingInstructions: next },
              () => saveSiteVisitSamplingInstructions(record.id, next),
              "Sampling instructions updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Client Instructions</span>,
      key: "clientInstructions",
      width: COL_W.clientInstructions,
      render: (_, record) => (
        <InlineText
          value={record?.visit?.interviewDetails?.clientInstructions}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commitInterview(
              record,
              { clientInstructions: next },
              () => saveSiteVisitClientInstructions(record.id, next),
              "Client instructions updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Other Instructions</span>,
      key: "otherInstructions",
      width: COL_W.otherInstructions,
      render: (_, record) => (
        <InlineText
          value={record?.visit?.interviewDetails?.otherInstructions}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commitInterview(
              record,
              { otherInstructions: next },
              () => saveSiteVisitOtherInstructions(record.id, next),
              "Other instructions updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">On-site Operation Specifics</span>,
      key: "specificsOfOnSiteOperation",
      width: COL_W.specifics,
      render: (_, record) => (
        <InlineText
          value={record?.specificsOfOnSiteOperation}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commit(
              record,
              { specificsOfOnSiteOperation: next },
              () => saveSiteVisitSpecifics(record.id, next),
              "On-site operation specifics updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Data Collection Account</span>,
      key: "fulcrumAccount",
      width: COL_W.fulcrumAccount,
      render: (_, record) => (
        <InlineText
          value={record?.logistic?.fulcrumAccount}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          allowEmpty
          onSave={(next) =>
            commitLogistic(
              record,
              { fulcrumAccount: next },
              () => saveSiteVisitFulcrumAccount(record.id, next),
              "Data collection account updated."
            )
          }
        />
      ),
    },
  ];

  /* ---------------------------------------------------------------- *
   * Implementation
   * ---------------------------------------------------------------- */
  const implementationColumns = [
    {
      title: <span className="styledTitle">Reporting Month</span>,
      key: "reportingMonth",
      width: COL_W.reportingMonth,
      sorter: byNumber((row) => row?.tracking?.reportingMonth),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineSelect
            value={Number(record.tracking.reportingMonth) || undefined}
            options={MONTH_OPTIONS}
            disabled={!canEdit}
            display={text(months[Number(record.tracking.reportingMonth)])}
            dropdownWidth={160}
            minWidth={140}
            onSave={(next) =>
              commitTracking(
                record,
                { reportingMonth: next },
                () => saveTrackingReportingMonth(record.id, next),
                "Reporting month updated."
              )
            }
          />
        )),
    },
    {
      title: <span className="styledTitle">Reporting Year</span>,
      key: "reportingYear",
      width: COL_W.reportingYear,
      sorter: byNumber((row) => row?.tracking?.reportingYear),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineSelect
            value={Number(record.tracking.reportingYear) || undefined}
            options={YEAR_OPTIONS}
            disabled={!canEdit}
            display={text(record.tracking.reportingYear)}
            dropdownWidth={140}
            minWidth={120}
            onSave={(next) =>
              commitTracking(
                record,
                { reportingYear: next },
                () => saveTrackingReportingYear(record.id, next),
                "Reporting year updated."
              )
            }
          />
        )),
    },
    {
      title: <span className="styledTitle">Training Completed</span>,
      key: "trainingIsCompleted",
      width: COL_W.trainingIsCompleted,
      sorter: byBool((row) => row?.tracking?.trainingIsCompleted),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineSelect
            value={!!record.tracking.trainingIsCompleted}
            options={YES_NO_OPTIONS}
            disabled={!canEdit}
            display={text(record.tracking.trainingIsCompleted ? "Yes" : "No")}
            dropdownWidth={120}
            minWidth={100}
            onSave={(next) =>
              commitTracking(
                record,
                { trainingIsCompleted: next },
                () => saveTrackingTrainingCompleted(record.id, next),
                "Training completed updated."
              )
            }
          />
        )),
    },
    {
      // The API keeps a list of training dates; this edits the first one and
      // then refetches, so whatever the server ends up storing is what shows.
      title: <span className="styledTitle">Training Date</span>,
      key: "trainingDate",
      width: COL_W.trainingDate,
      sorter: byText(
        (row) => row?.tracking?.trainingDate || row?.tracking?.trainingDates?.[0]?.date
      ),
      render: (_, record) =>
        trackingCell(record, () => {
          const value =
            record.tracking.trainingDate ||
            record.tracking.trainingDates?.[0]?.date;
          return (
            <InlineDate
              value={value}
              disabled={!canEdit}
              format={DATE_FORMAT}
              onSave={(next) =>
                commitTracking(
                  record,
                  { trainingDate: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
                  () => saveTrackingTrainingDate(record.id, next),
                  "Training date updated.",
                  { refresh: true }
                )
              }
            />
          );
        }),
    },
    {
      title: <span className="styledTitle">Training Score Info</span>,
      key: "trainingScoreInfo",
      width: COL_W.trainingScoreInfo,
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineText
            value={record.tracking.trainingScoreInfo}
            disabled={!canEdit}
            maxWidth={TEXT_COL_MAX}
            allowEmpty
            onSave={(next) =>
              commitTracking(
                record,
                { trainingScoreInfo: next },
                () => saveTrackingTrainingScoreInfo(record.id, next),
                "Training score info updated."
              )
            }
          />
        )),
    },
    {
      // Same list handling as the training date: a visit can carry several
      // actual dates, so all of them are shown and the cell edits the primary.
      title: <span className="styledTitle">Actual Date of Visit</span>,
      key: "actualDateOfVisit",
      width: COL_W.actualDateOfVisit,
      sorter: byText(
        (row) =>
          row?.tracking?.actualDateOfVisit ||
          row?.tracking?.siteTrackingVisitDates?.[0]?.visitDate
      ),
      render: (_, record) =>
        trackingCell(record, () => {
          const visitDates = record.tracking.siteTrackingVisitDates || [];
          const value =
            record.tracking.actualDateOfVisit || visitDates[0]?.visitDate;
          const extra = visitDates.slice(1);
          return (
            <InlineDate
              value={value}
              disabled={!canEdit}
              format={DATE_FORMAT}
              display={
                extra.length ? (
                  <Tooltip
                    title={extra
                      .map((date) => formatDate(date?.visitDate))
                      .filter(Boolean)
                      .join(", ")}
                  >
                    <span className="styledDataIndex3">
                      {formatDate(value) || "—"} +{extra.length}
                    </span>
                  </Tooltip>
                ) : undefined
              }
              onSave={(next) =>
                commitTracking(
                  record,
                  { actualDateOfVisit: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
                  () => saveTrackingActualDateOfVisit(record.id, next),
                  "Actual date of visit updated.",
                  { refresh: true }
                )
              }
            />
          );
        }),
    },
    {
      title: <span className="styledTitle">% Data Collection Completed</span>,
      key: "percentageOfDataCollectionCompleted",
      width: COL_W.percentageCompleted,
      sorter: byNumber((row) => row?.tracking?.percentageOfDataCollectionCompleted),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineNumber
            value={record.tracking.percentageOfDataCollectionCompleted}
            disabled={!canEdit}
            min={0}
            max={100}
            suffix="%"
            onSave={(next) =>
              commitTracking(
                record,
                { percentageOfDataCollectionCompleted: next },
                () => saveTrackingPercentageCompleted(record.id, next),
                "% of data collection completed updated."
              )
            }
          />
        )),
    },
    {
      title: <span className="styledTitle">Postponed</span>,
      key: "isPostponed",
      width: COL_W.isPostponed,
      sorter: byBool((row) => row?.logistic?.isPostponed),
      render: (_, record) => (
        <InlineSelect
          value={!!record?.logistic?.isPostponed}
          options={YES_NO_OPTIONS}
          disabled={!canEdit}
          display={text(record?.logistic?.isPostponed ? "Yes" : "No")}
          dropdownWidth={120}
          minWidth={100}
          onSave={(next) =>
            commitLogistic(
              record,
              { isPostponed: next },
              () => saveSiteVisitPostponed(record.id, next),
              "Postponed updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Postponed Reason</span>,
      key: "postponedReason",
      width: COL_W.postponedReason,
      render: (_, record) => (
        <InlineText
          value={record?.logistic?.postponedReason}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          allowEmpty
          onSave={(next) =>
            commitLogistic(
              record,
              { postponedReason: next },
              () => saveSiteVisitPostponedReason(record.id, next),
              "Postponed reason updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Postponed Details</span>,
      key: "postponedDetails",
      width: COL_W.postponedDetails,
      render: (_, record) => (
        <InlineText
          value={record?.logistic?.postponedDetails}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commitLogistic(
              record,
              { postponedDetails: next },
              () => saveSiteVisitPostponedDetails(record.id, next),
              "Postponed details updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Notes</span>,
      key: "notes",
      width: COL_W.notes,
      render: (_, record) => (
        <InlineText
          value={record?.logistic?.notes}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commitLogistic(
              record,
              { notes: next },
              () => saveSiteVisitNotes(record.id, next),
              "Notes updated."
            )
          }
        />
      ),
    },
  ];

  /* ---------------------------------------------------------------- *
   * Reporting
   * ---------------------------------------------------------------- */
  const reportingColumns = [
    {
      title: <span className="styledTitle">Field Report Status</span>,
      key: "fieldTeamReportingStatus",
      width: COL_W.fieldTeamReportingStatus,
      sorter: byNumber((row) => row?.tracking?.fieldTeamReportingStatus),
      render: (_, record) =>
        trackingCell(record, () => {
          const current = record.tracking.fieldTeamReportingStatus;
          return (
            <InlineSelect
              value={current}
              options={FIELD_REPORT_STATUS_OPTIONS}
              disabled={!canEdit}
              display={
                current ? (
                  <Pill status={getFieldTeamReportingStatus(current)} />
                ) : (
                  text(null)
                )
              }
              dropdownWidth={280}
              minWidth={250}
              onSave={(next) =>
                commitTracking(
                  record,
                  { fieldTeamReportingStatus: next },
                  () =>
                    saveFieldTeamReportingStatus(record.tracking.id, next),
                  "Field report status updated."
                )
              }
            />
          );
        }),
    },
    {
      // Read-only: approving or rejecting is a gated transition and a rejection
      // carries a reason, so both stay on the approval screens.
      title: <span className="styledTitle">Approval Status</span>,
      dataIndex: "approvalStatus",
      key: "approvalStatus",
      width: COL_W.approvalStatus,
      sorter: byNumber((row) => row.approvalStatus),
      render: (approvalStatus, record) => {
        const approval = APPROVAL_PILL[approvalStatus];
        if (!approval) return text(null);
        const label = (
          <span className="styledDataIndex3" style={{ color: approval.fill }}>
            {approval.text}
          </span>
        );
        return record?.rejectionReason ? (
          <Tooltip title={`Reason: ${record.rejectionReason}`}>{label}</Tooltip>
        ) : (
          label
        );
      },
    },
    {
      title: <span className="styledTitle">Date of Report</span>,
      key: "dateOfReport",
      width: COL_W.dateOfReport,
      sorter: byText((row) => row?.tracking?.dateOfReport),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineDate
            value={record.tracking.dateOfReport}
            disabled={!canEdit}
            format={DATE_FORMAT}
            onSave={(next) =>
              commitTracking(
                record,
                { dateOfReport: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
                () => saveTrackingDateOfReport(record.id, next),
                "Date of report updated."
              )
            }
          />
        )),
    },
    {
      title: <span className="styledTitle">Date of Debrief</span>,
      key: "dateOfDebrief",
      width: COL_W.dateOfDebrief,
      sorter: byText((row) => row?.tracking?.dateOfDebrief),
      render: (_, record) =>
        trackingCell(record, () => (
          <InlineDate
            value={record.tracking.dateOfDebrief}
            disabled={!canEdit}
            format={DATE_FORMAT}
            onSave={(next) =>
              commitTracking(
                record,
                { dateOfDebrief: dayjs(next).format("YYYY-MM-DD HH:mm:ss") },
                () => saveTrackingDateOfDebrief(record.id, next),
                "Date of debrief updated."
              )
            }
          />
        )),
    },
    {
      title: <span className="styledTitle">Link to Data Collection Results</span>,
      key: "linkToDataCollectionResults",
      width: COL_W.dataCollectionLink,
      render: (_, record) => (
        <InlineText
          value={record?.linkToDataCollectionResults}
          disabled={!canEdit}
          maxWidth={TEXT_COL_MAX}
          maxLength={500}
          allowEmpty
          onSave={(next) =>
            commit(
              record,
              { linkToDataCollectionResults: next },
              () => saveSiteVisitDataCollectionLink(record.id, next),
              "Link to data collection results updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Photos Required</span>,
      dataIndex: "hasPhotos",
      key: "hasPhotos",
      width: COL_W.photosRequired,
      sorter: byBool((row) => row.hasPhotos),
      render: (hasPhotos, record) => (
        <InlineSelect
          value={!!hasPhotos}
          options={YES_NO_OPTIONS}
          disabled={!canEdit}
          display={text(hasPhotos ? "Yes" : "No")}
          dropdownWidth={120}
          minWidth={100}
          onSave={(next) =>
            commit(
              record,
              { hasPhotos: next, photoCount: next ? record.photoCount || 0 : 0 },
              () =>
                saveSiteVisitPhotos(record.id, {
                  required: next,
                  count: record.photoCount,
                }),
              "Photos required updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Photos</span>,
      dataIndex: "photoCount",
      key: "photoCount",
      width: COL_W.photoCount,
      sorter: byNumber((row) => row.photoCount),
      render: (photoCount, record) => (
        <InlineNumber
          value={photoCount ?? 0}
          disabled={!canEdit}
          width={80}
          onSave={(next) =>
            commit(
              record,
              // A count above zero is what makes the media required — the
              // reporting form couples the two the same way.
              { photoCount: next, hasPhotos: next > 0 },
              () =>
                saveSiteVisitPhotos(record.id, {
                  required: next > 0,
                  count: next,
                }),
              "Photo count updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Videos Required</span>,
      dataIndex: "hasVideos",
      key: "hasVideos",
      width: COL_W.videosRequired,
      sorter: byBool((row) => row.hasVideos),
      render: (hasVideos, record) => (
        <InlineSelect
          value={!!hasVideos}
          options={YES_NO_OPTIONS}
          disabled={!canEdit}
          display={text(hasVideos ? "Yes" : "No")}
          dropdownWidth={120}
          minWidth={100}
          onSave={(next) =>
            commit(
              record,
              { hasVideos: next, videoCount: next ? record.videoCount || 0 : 0 },
              () =>
                saveSiteVisitVideos(record.id, {
                  required: next,
                  count: record.videoCount,
                }),
              "Videos required updated."
            )
          }
        />
      ),
    },
    {
      title: <span className="styledTitle">Videos</span>,
      dataIndex: "videoCount",
      key: "videoCount",
      width: COL_W.videoCount,
      sorter: byNumber((row) => row.videoCount),
      render: (videoCount, record) => (
        <InlineNumber
          value={videoCount ?? 0}
          disabled={!canEdit}
          width={80}
          onSave={(next) =>
            commit(
              record,
              { videoCount: next, hasVideos: next > 0 },
              () =>
                saveSiteVisitVideos(record.id, {
                  required: next > 0,
                  count: next,
                }),
              "Video count updated."
            )
          }
        />
      ),
    },
  ];

  /**
   * The workflow driver: one column that says what this visit is waiting on and
   * does it.
   *
   * Without it, finishing a site visit meant the reporting list to submit, the
   * approval screen to approve, and the approved list to close out — three
   * screens for three clicks. The button performs the recommended step; the
   * caret holds every other step that is legal for the row right now.
   */
  const nextStepColumn = {
    // The header carries the fold control. Pinned columns are what the scrollable
    // area pays for, so this one can be folded down to its icon on a narrow screen
    // and opened back up when there is room.
    title: (
      <span className="nextStepHead">
        {!nextStepCollapsed && <span className="styledTitle">Next Step</span>}
        <Tooltip
          title={
            nextStepCollapsed
              ? "Expand the Next Step column"
              : "Collapse the Next Step column — the steps stay reachable from its icon"
          }
        >
          <button
            type="button"
            className="nextStepHead__toggle"
            aria-label={
              nextStepCollapsed
                ? "Expand the Next Step column"
                : "Collapse the Next Step column"
            }
            // The header cell is also the sort control, so the fold must not
            // count as a click on it.
            onClick={(event) => {
              event.stopPropagation();
              setNextStepCollapsed((prev) => !prev);
            }}
          >
            {nextStepCollapsed ? (
              <FiChevronsLeft size={13} />
            ) : (
              <FiChevronsRight size={13} />
            )}
          </button>
        </Tooltip>
      </span>
    ),
    key: "nextStep",
    width: nextStepCollapsed ? COL_W.nextStepCollapsed : COL_W.nextStep,
    fixed: "right",
    sorter: (a, b) =>
      nextSiteVisitStep(a).label.localeCompare(nextSiteVisitStep(b).label),
    render: (_, record) => {
      const step = nextSiteVisitStep(record);
      const actions = workflowActions(record);

      if (step.key === "done") {
        return (
          <Tooltip title={step.tooltip}>
            <span className="siteVisitDone">
              <FiCheck size={13} />
              {!nextStepCollapsed && <span>Completed</span>}
            </span>
          </Tooltip>
        );
      }

      // Nothing this role may do — say so rather than showing a dead control.
      if (!actions.length) {
        return (
          <Tooltip title={`${step.label} — your role cannot advance this site visit.`}>
            {text(null, nextStepCollapsed ? "—" : step.label)}
          </Tooltip>
        );
      }

      // The recommended step, when this role is allowed it, otherwise whatever
      // it can do next.
      const primary =
        actions.find((action) => action.key === step.key) ||
        actions.find((action) => action.key === "submit") ||
        actions[0];
      const rest = actions.filter((action) => action.key !== primary.key);

      const menuItems = (entries) =>
        entries.map((action) => ({
          key: action.key,
          label: action.label,
          danger: action.danger,
        }));

      const runAction = ({ key }) =>
        actions.find((action) => action.key === key)?.run();

      // Collapsed, every step moves into the menu with the recommended one at the
      // top: one click to open, one to act, and the tooltip still names the step
      // so the column reads at a glance.
      if (nextStepCollapsed) {
        return (
          <Tooltip title={`${primary.label} — ${step.tooltip}`}>
            <span className="nextStepCell nextStepCell--collapsed">
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: menuItems(actions),
                  onClick: runAction,
                  className: "nextStepMenu",
                }}
              >
                <button
                  type="button"
                  className="nextStepCell__icon"
                  aria-label={primary.label}
                >
                  <FiChevronsRight size={14} />
                </button>
              </Dropdown>
            </span>
          </Tooltip>
        );
      }

      return (
        <Tooltip title={step.tooltip}>
          <span className="nextStepCell">
            <Dropdown.Button
              size="small"
              className="nextStepCell__button"
              onClick={primary.run}
              trigger={["click"]}
              icon={<FiChevronDown size={13} />}
              menu={{
                items: menuItems(rest),
                onClick: runAction,
                className: "nextStepMenu",
              }}
            >
              {primary.label}
            </Dropdown.Button>
          </span>
        </Tooltip>
      );
    },
  };

  const actionColumn = {
    key: "actions",
    width: COL_W.actions,
    // Pinned alongside Next Step: antd expects the right-hand fixed columns to
    // be contiguous, and both are wanted at any scroll position anyway.
    fixed: "right",
    render: (_, record) => (
      <div className="actionDiv">
        <Tooltip title="Preview site visit">
          <img
            src={previewIcon}
            style={{ cursor: "pointer" }}
            alt="previewicon"
            onClick={() => handleTogglePreviewModal(record.id)}
          />
        </Tooltip>
        {/* Inline cells cover every single-field edit; these open the two forms
            that own the values a cell cannot save on its own. */}
        {canEdit && (
          <>
            <Tooltip title="Edit full site visit card (location, IP plan, contacts, files)">
              <img
                src={editIcon}
                style={{ cursor: "pointer" }}
                alt="editicon"
                onClick={() => handleOpenEditModal(record.id)}
              />
            </Tooltip>
            {record?.visit?.id && (
              <Tooltip title="Edit data checks (conducted vs target)">
                <FiCheckSquare
                  size={17}
                  color="#AA1A5F"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setDataChecksTarget({
                      siteVisitId: record.id,
                      visitId: record.visit.id,
                    })
                  }
                />
              </Tooltip>
            )}
          </>
        )}
      </div>
    ),
  };

  // Phase bands are what make ~40 columns navigable: the top header row says
  // which part of the process a block of columns belongs to.
  //
  // Each band's children run through the shared order so this table reads in the
  // same sequence as the Planning, Implementation, Reporting and Completed
  // tables — someone moving between them finds the columns where they expect.
  const phaseGroup = (key, label, children) => ({
    key: `phase-${key}`,
    title: <span className="phaseGroup__label">{label}</span>,
    onHeaderCell: () => ({ className: `phaseGroup phaseGroup--${key}` }),
    children: orderSiteVisitColumns(children),
  });

  const columns = [
    ...identityColumns,
    ...(visiblePhases.planning
      ? [phaseGroup("planning", "Planning", planningColumns)]
      : []),
    ...(visiblePhases.implementation
      ? [phaseGroup("implementation", "Implementation", implementationColumns)]
      : []),
    ...(visiblePhases.reporting
      ? [phaseGroup("reporting", "Reporting", reportingColumns)]
      : []),
    // Outside the phase bands and pinned right: the next step applies to the
    // whole lifecycle, not to one phase, and it should stay reachable however
    // far the 40-odd columns have been scrolled.
    nextStepColumn,
    actionColumn,
  ];

  // Per-user widths and folded columns. This is the table that needs them most:
  // 40 columns, of which the long free-text ones (instructions, notes) are the
  // reason the rest scroll off screen. `useColumnSizing` walks into the phase
  // bands' children, and leaves any column nobody has touched exactly as
  // declared so the `table-layout: auto` fitting still applies to it.
  const {
    columns: sizedColumns,
    components: tableComponents,
    sizingBar,
  } = useColumnSizing({ tableId: "siteVisitSummary", columns });

  const rowSelection = {
    // Pinned with the identity columns: antd expects the left-hand fixed block to
    // start at the first column, and a checkbox that scrolled away from its own
    // row would be worse than useless anyway.
    fixed: true,
    onChange: (selectedRowKeys) => onRowSelectionChange(selectedRowKeys),
  };

  return (
    <>
      <Filters
        filters={filters}
        setFilters={setFilters}
        setPagination={setPagination}
      />

      <div
        style={{
          color: "#AA1A5F",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "24px",
          margin: "15px 0",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "0.5rem",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        Total: <span className="draft-count">{pagination.total} visits</span>
        {canEdit && (
          <span style={{ color: "#777", fontSize: "12px", fontWeight: 400 }}>
            Click any cell to edit it — Enter saves text, picking an option saves
            straight away.
          </span>
        )}
        {sizingBar && (
          <span style={{ marginLeft: "auto" }}>{sizingBar}</span>
        )}
        <span
          className="phaseToggle"
          style={sizingBar ? undefined : { marginLeft: "auto" }}
        >
          <span className="phaseToggle__label">Phases:</span>
          {PHASES.map((phase) => (
            <Checkbox
              key={phase.key}
              className="phaseToggle__item"
              checked={visiblePhases[phase.key]}
              onChange={(e) =>
                setVisiblePhases((prev) => ({
                  ...prev,
                  [phase.key]: e.target.checked,
                }))
              }
            >
              {phase.label}
            </Checkbox>
          ))}
        </span>
      </div>
      {/* The full process is a wide table, so its own scrollbar sits far below
          the fold — it is mirrored above the header too. `sticky` additionally
          pins the header rows and gives antd's scrollbar to the bottom of the
          viewport, so sideways scrolling is in reach from anywhere in the grid. */}
      <TableScrollSync
        syncKey={`${rows.length}-${pagination.current}-${Object.values(
          visiblePhases
        ).join("")}`}
      >
        <Table
          components={tableComponents}
          rowKey="id"
          // `auto` + max-content: every column ends up as wide as its widest
          // value instead of being clipped to a fixed width.
          tableLayout="auto"
          rowSelection={rows.length > 0 ? rowSelection : ""}
          columns={rows?.length > 0 ? sizedColumns : []}
          dataSource={rows}
          loading={loading}
          className="tableTPM siteVisitSummaryTable"
          // The phase band labels stick to the left of the scrollport, which the
          // pinned identity block now covers — this is where they park instead.
          style={{ "--phaseLabelOffset": `${FROZEN_LEFT_W + 12}px` }}
          scroll={{ x: "max-content" }}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            ...pagination,
            onChange: onPageChange,
            showSizeChanger: true,
          }}
        />
      </TableScrollSync>
      <Preview
        visible={showPreviewModal}
        onClose={handleTogglePreviewModal}
        siteVisitCardId={selectedSiteVisit}
      />
      <EditSiteVisitCardModal
        visible={showEditModal}
        siteVisitCardId={selectedSiteVisit}
        onCancel={() => setShowEditModal(false)}
        onOk={() => {
          setShowEditModal(false);
          // A modal save can touch fields no cell shows, so refetch rather than
          // patching the row.
          onRefresh?.();
        }}
        setMessage={setModalMessage}
        allowEditStatus={false}
      />
      <EditDataChecksModal
        visible={!!dataChecksTarget}
        siteVisitCardId={dataChecksTarget?.siteVisitId}
        visitId={dataChecksTarget?.visitId}
        onCancel={() => setDataChecksTarget(null)}
        onOk={() => {
          setDataChecksTarget(null);
          onRefresh?.();
        }}
        setMessage={setModalMessage}
      />
      {/* Tracking is what moves a visit out of planning, so the summary can
          create it as well as edit it — the same modal the Implementation board
          opens. */}
      <AddTrackingCardModal
        visible={!!trackingTarget}
        siteVisitCardId={trackingTarget?.id}
        tracked={!!trackingTarget?.tracked}
        onCancel={() => setTrackingTarget(null)}
        onOk={() => {
          setTrackingTarget(null);
          onRefresh?.();
        }}
        setMessage={setModalMessage}
        fetchSiteVisitCards={onRefresh}
      />
      {/* Report documents, photo/video files and the dataset link. */}
      <EditReportingModal
        visible={!!reportTarget}
        siteVisitCardId={reportTarget}
        onCancel={() => setReportTarget(null)}
        onOk={() => {
          setReportTarget(null);
          onRefresh?.();
        }}
        setMessage={setModalMessage}
        fetchSiteVisits={onRefresh}
      />
      {/* Rejecting needs a reason, so it goes through the approval screen's own
          modal rather than a bare confirm. */}
      <RejectModal
        visible={!!rejectTarget}
        siteVisitCardId={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onOk={() => {
          setRejectTarget(null);
          onRefresh?.();
        }}
        setMessage={setModalMessage}
      />
    </>
  );
}

export default TableView;
